/**
 * UIAssetLoader - Fetches HTML templates and CSS fragments with simple caching to avoid
 * repeated network requests. Supports clearing cache for development/hot reloading.
 */

import { projectInfo, resolveAssetPathsInText } from '../storage/projectContext.js';

import type { AssetPath } from '../storage/storageProvider.js';


export class UIAssetLoader {
  private static cache = new Map<string, string>();

  /**
   * Load a file from the specified path
   * @param path - Complete path to the file
   * @returns Promise that resolves to the file content
   */
  static async loadFile(path: AssetPath): Promise<string> {
    if (this.cache.has(path.initialPath)) {
      return this.cache.get(path.initialPath)!;
    }
    try {
      const content = await projectInfo.storageProvider.downloadFileAsText(path);
      const resolvedContent = await resolveAssetPathsInText(content);
      this.cache.set(path.initialPath, resolvedContent);
      return resolvedContent;
    } catch (error) {
      console.error(`Error loading file ${path}:`, error);
      throw error;
    }
  }

  /**
   * Load an HTML template from a file
   * @param path - Complete path to the template file
   * @returns Promise that resolves to the template content
   */
  static async loadTemplate(path: AssetPath): Promise<string> {
    return this.loadFile(path);
  }

  /**
   * Load CSS styles from a file
   * @param path - Complete path to the style file
   * @returns Promise that resolves to the CSS content
   */
  static async loadStyles(path: AssetPath): Promise<string> {
    return this.loadFile(path);
  }

  /**
   * Load both template and styles for a UI component
   * @param templatePath - Complete path to the template file
   * @param stylePath - Complete path to the style file
   * @returns Promise that resolves to an object containing both template and styles
   */
  static async loadUIAsset(templatePath: AssetPath, stylePath: AssetPath): Promise<{ template: string; styles: string }> {
    const [template, styles] = await Promise.all([
      this.loadTemplate(templatePath),
      this.loadStyles(stylePath),
    ]);

    return { template, styles };
  }

  /**
   * Clear the cache (useful for development/hot reloading)
   */
  static clearCache(): void {
    this.cache.clear();
  }
}
